import React, { useState, useEffect } from 'react';
import propertyService from '../../services/propertyService';
import Papa from 'papaparse';

const ManualEntryTab = ({ onPropertyAdded }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  
  // CSV Import States
  const [csvFile, setCsvFile] = useState(null);
  const [isParsingCsv, setIsParsingCsv] = useState(false);
  const [csvParseError, setCsvParseError] = useState('');
  const [csvPreview, setCsvPreview] = useState(null);
  const [draggedImageIndex, setDraggedImageIndex] = useState(null);

  // Form state
  const [propertyData, setPropertyData] = useState({
    address: '',
    neighborhood: '',
    price: '',
    bedrooms: '',
    bathrooms: '',
    sqft: '',
    lotSize: '',
    description: '',
    propertyType: 'House',
    listingType: 'purchase',
    yearBuilt: '',
    yearRenovated: '',
    latitude: '',
    longitude: '',
    propertyStatus: 'For Sale',
    daysOnMarket: '0',
    hoaFees: '0',
    propertyTax: '0',
    features: '',
    agentName: '',
    agency: '',
    contactPhone: '',
    contactEmail: '',
    isAvailable: true,
    availableDate: new Date().toISOString().split('T')[0],
    allowsDogs: false,
    allowsCats: false,
    petDeposit: '0',
    petRent: '0',
    petRestrictions: '',
    parkingType: 'None',
    parkingSpaces: '0',
    parkingDescription: '',
    parkingIncluded: true,
    parkingFee: '0',
    laundryType: 'None',
    laundryDescription: '',
    laundryIncluded: true,
    garageType: 'None',
    garageSpaces: '0',
  });
  
  const [neighborhoods, setNeighborhoods] = useState([]);
  
  // Images state
  const [mainImage, setMainImage] = useState('');
  const [propertyImages, setPropertyImages] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploadAreaHovered, setIsUploadAreaHovered] = useState(false);
  const [isButtonHovered, setIsButtonHovered] = useState(false);

  useEffect(() => {
    fetchNeighborhoods();
    fetchAgents();
  }, []);
  
  const fetchNeighborhoods = async () => {
    try {
      const response = await propertyService.getNeighborhoods();
      if (response && response.data) {
        setNeighborhoods(response.data);
      }
    } catch (error) {
      console.error('Error fetching neighborhoods:', error);
      setErrorMessage(`Failed to load neighborhoods: ${error.message || 'Unknown error'}`);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await propertyService.getAgentRoster();
      if (response && response.data) {
        setAgents(response.data);
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
      setErrorMessage(`Failed to load agent roster: ${error.message || 'Unknown error'}`);
    }
  };

  const handleAgentSelect = (e) => {
    const agentId = e.target.value;
    setSelectedAgent(agentId);
    
    if (!agentId) {
      setPropertyData({
        ...propertyData,
        agentName: '',
        agency: '',
        contactPhone: '',
        contactEmail: ''
      });
      return;
    }
    
    const agent = agents.find(a => a._id === agentId);
    
    if (agent) {
      setPropertyData({
        ...propertyData,
        agentName: agent.name || '',
        agency: agent.agentInfo?.agency || agent.agency || '',
        contactPhone: agent.phone || '',
        contactEmail: agent.email || ''
      });
    }
  };

  // CSV Handling Functions
  const handleCsvFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        setCsvParseError('Please upload a valid CSV file');
        setCsvFile(null);
        return;
      }
      
      setCsvFile(file);
      setCsvParseError('');
      setCsvPreview({
        name: file.name,
        size: (file.size / 1024).toFixed(2) + ' KB'
      });
    }
  };

  const parseCsvFile = () => {
    if (!csvFile) {
      setCsvParseError('Please select a CSV file first');
      return;
    }
    
    setIsParsingCsv(true);
    setCsvParseError('');
    
    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      complete: handleCsvParseComplete,
      error: (error) => {
        setCsvParseError(`Error parsing CSV: ${error.message}`);
        setIsParsingCsv(false);
      }
    });
  };

  const handleCsvParseComplete = (results) => {
    if (results.errors.length > 0) {
      setCsvParseError(`Error parsing CSV: ${results.errors[0].message}`);
      setIsParsingCsv(false);
      return;
    }
    
    if (results.data.length === 0) {
      setCsvParseError('CSV file is empty');
      setIsParsingCsv(false);
      return;
    }
    
    const propertyRow = results.data[0];
    const requiredFields = ['Address', 'Price', 'Bedrooms', 'Bathrooms', 'SqFt'];
    const missingFields = requiredFields.filter(field => !propertyRow[field]);
    
    if (missingFields.length > 0) {
      setCsvParseError(`CSV is missing required fields: ${missingFields.join(', ')}`);
      setIsParsingCsv(false);
      return;
    }
    
    try {
      const updatedPropertyData = {
        ...propertyData,
        address: propertyRow['Address'] ? propertyRow['Address'].toString().split(',')[0].trim() : '',
        price: propertyRow['Price'] ? propertyRow['Price'].toString().replace(/USD|\$/g, '').trim() : '',
        bedrooms: propertyRow['Bedrooms'] ? propertyRow['Bedrooms'].toString().replace(/[^\d]/g, '') : '',
        bathrooms: propertyRow['Bathrooms'] ? propertyRow['Bathrooms'].toString().replace(/[^\d.]/g, '') : '',
        sqft: propertyRow['SqFt'] ? propertyRow['SqFt'].toString().replace(/[^\d]/g, '') : '',
        description: propertyRow['Description'] || '',
        yearBuilt: propertyRow['Year Built'] || '',
        lotSize: propertyRow['Lot Size'] ? 
          Math.round(parseFloat(propertyRow['Lot Size'].toString().replace(/[^\d.]/g, '')) * 43560).toString() : '',
        propertyTax: propertyRow['Annual Property Taxes'] ? 
          propertyRow['Annual Property Taxes'].toString().replace(/[^\d.]/g, '') : '0',
        hoaFees: propertyRow['HOA Fees'] ? 
          propertyRow['HOA Fees'].toString().replace(/[^\d.]/g, '') : '0',
      };
      
      setPropertyData(updatedPropertyData);
      setSuccessMessage('CSV data loaded successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error processing CSV data:', error);
      setCsvParseError(`Error processing CSV data: ${error.message}`);
    }
    
    setIsParsingCsv(false);
  };

  // Image drag and drop handlers
  const handleDragStart = (index) => {
    setDraggedImageIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedImageIndex === null) return;
    
    const newImages = [...propertyImages];
    const draggedImage = newImages[draggedImageIndex];
    
    newImages.splice(draggedImageIndex, 1);
    newImages.splice(dropIndex, 0, draggedImage);
    
    setPropertyImages(newImages);
    setDraggedImageIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedImageIndex(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (['price', 'hoaFees', 'propertyTax'].includes(name)) {
      const numeric = value.replace(/[^0-9.]/g, '');
      const withCommas = Number(numeric).toLocaleString('en-US');
      
      let finalValue = withCommas;
      if (name === 'price' && propertyData.listingType === 'rent') {
        finalValue += '/mo';
      }
      
      setPropertyData({
        ...propertyData,
        [name]: finalValue
      });
      return;
    }
    
    if (name === 'listingType') {
      const isRental = value === 'rent';
      const numeric = propertyData.price.replace(/[^0-9.]/g, '');
      const withCommas = Number(numeric).toLocaleString('en-US');
      const formattedPrice = isRental ? `${withCommas}/mo` : withCommas;
      
      setPropertyData({
        ...propertyData,
        listingType: value,
        price: formattedPrice
      });
      return;
    }
    
    setPropertyData({
      ...propertyData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const sqftToAcres = (sqft) => {
    const numericSqft = parseFloat(sqft) || 0;
    return Number((numericSqft / 43560).toFixed(2));
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploadProgress(0);
    setErrorMessage('');
    
    try {
      const cloudinaryUrls = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);
        formData.append('upload_preset', 'uploads'); 
        
        const response = await fetch(`https://api.cloudinary.com/v1_1/dckv4itgt/image/upload`, {
          method: 'POST',
          body: formData
        });
        
        if (!response.ok) {
          throw new Error(`Cloudinary upload failed: ${response.statusText}`);
        }
        
        const data = await response.json();
        const imageUrl = data.secure_url;
        cloudinaryUrls.push(imageUrl);
        setPropertyImages(prev => [...prev, imageUrl]);
        setUploadProgress(Math.round((i + 1) / files.length * 100));
      }
      
      if (!mainImage && cloudinaryUrls.length > 0) {
        setMainImage(cloudinaryUrls[0]);
      }
      
    } catch (error) {
      console.error('Error uploading images:', error);
      setErrorMessage(`Failed to upload images: ${error.message || 'Unknown error'}`);
    }
  };

  const setAsMainImage = (imageUrl) => {
    setMainImage(imageUrl);
  };

  const removeImage = (imageUrl) => {
    setPropertyImages(propertyImages.filter(img => img !== imageUrl));
    
    if (mainImage === imageUrl) {
      setMainImage(propertyImages.length > 1 ? 
        propertyImages.find(img => img !== imageUrl) : '');
    }
  };

  const validateForm = () => {
    const requiredFields = ['address', 'neighborhood', 'price', 'bedrooms', 'bathrooms', 'sqft', 'propertyType', 'description'];
    const missingFields = requiredFields.filter(field => !propertyData[field]);
    
    if (missingFields.length > 0) {
      setErrorMessage(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return false;
    }
    
    if (propertyImages.length < 5) {
      setErrorMessage('Please upload at least 5 property images');
      return false;
    }
    
    if (!mainImage) {
      setErrorMessage('Please select a main image');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setSuccessMessage('');
    setErrorMessage('');
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      const extractNumericPrice = (priceString) => {
        const numericPart = priceString.replace(/[^0-9.]/g, '');
        return parseFloat(numericPart) || 0;
      };

      const formattedData = {
        address: propertyData.address.trim(),
        neighborhood: propertyData.neighborhood.trim(),
        price: propertyData.price.startsWith('$') ? 
          propertyData.price : `$${propertyData.price}`,
        numericPrice: extractNumericPrice(propertyData.price),
        bedrooms: parseInt(propertyData.bedrooms, 10),
        bathrooms: parseFloat(propertyData.bathrooms),
        sqft: parseInt(propertyData.sqft, 10),
        lotSize: sqftToAcres(propertyData.lotSize),
        description: propertyData.description,
        propertyType: propertyData.propertyType,
        listingType: propertyData.listingType,
        propertyStatus: propertyData.propertyStatus,
        daysOnMarket: parseInt(propertyData.daysOnMarket || 0, 10),
        hoaFees: parseInt(propertyData.hoaFees || 0, 10),
        propertyTax: parseInt(propertyData.propertyTax || 0, 10),
        yearBuilt: parseInt(propertyData.yearBuilt || 0, 10),
        yearRenovated: propertyData.yearRenovated ? parseInt(propertyData.yearRenovated, 10) : 0,
        location: {
          type: 'Point',
          coordinates: [
            parseFloat(propertyData.longitude || -104.9903),
            parseFloat(propertyData.latitude || 39.7392)
          ]
        },
        features: propertyData.features ? 
          propertyData.features.split(',').map(item => item.trim()) : [],
        mainImage: mainImage,
        images: propertyImages,
        isAvailable: propertyData.isAvailable,
        availableDate: propertyData.availableDate,
        
        petPolicy: {
          allowsDogs: propertyData.allowsDogs,
          allowsCats: propertyData.allowsCats,
          petDeposit: parseInt(propertyData.petDeposit || 0, 10),
          petRent: parseInt(propertyData.petRent || 0, 10),
          petRestrictions: propertyData.petRestrictions
        },
        
        parking: {
          type: propertyData.parkingType,
          spaces: parseInt(propertyData.parkingSpaces || 0, 10),
          description: propertyData.parkingDescription || '',
          isIncluded: propertyData.parkingIncluded,
          additionalFee: parseInt(propertyData.parkingFee || 0)
        },
        
        laundry: {
          type: propertyData.laundryType,
          description: propertyData.laundryDescription || '',
          isIncluded: propertyData.laundryIncluded
        },
        
        garage: {
          type: propertyData.garageType,
          spaces: parseInt(propertyData.garageSpaces || 0, 10)
        }
      };
      
      // Add agent info, either from a selected agent or manual entry
      if (selectedAgent) {
        // If an agent was selected from the dropdown, set both the agent reference and the listedBy display info
        formattedData.agent = selectedAgent; // Reference to the agent document
        
        // Still include the listedBy display information
        formattedData.listedBy = {
          agentId: selectedAgent,
          agentName: propertyData.agentName,
          agency: propertyData.agency,
          contactPhone: propertyData.contactPhone,
          contactEmail: propertyData.contactEmail
        };
      } else if (propertyData.agentName || propertyData.agency) {
        // If no agent was selected but manual info was provided, just set the listedBy display info
        formattedData.listedBy = {
          agentName: propertyData.agentName,
          agency: propertyData.agency,
          contactPhone: propertyData.contactPhone,
          contactEmail: propertyData.contactEmail
        };
      }
      
      const response = await propertyService.createProperty(formattedData);
      
      setSuccessMessage('Property created successfully!');
      
      if (onPropertyAdded) {
        onPropertyAdded();
      }
      
      resetForm();
      
    } catch (error) {
      console.error('Error creating property:', error);
      setErrorMessage('Failed to create property: ' + (error.message || 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setPropertyData({
      address: '',
      neighborhood: '',
      price: '',
      bedrooms: '',
      bathrooms: '',
      sqft: '',
      lotSize: '',
      description: '',
      propertyType: 'House',
      listingType: 'purchase',
      yearBuilt: '',
      yearRenovated: '',
      latitude: '',
      longitude: '',
      propertyStatus: 'For Sale',
      daysOnMarket: '0',
      hoaFees: '0',
      propertyTax: '0',
      features: '',
      agentName: '',
      agency: '',
      contactPhone: '',
      contactEmail: '',
      isAvailable: true,
      availableDate: new Date().toISOString().split('T')[0],
      allowsDogs: false,
      allowsCats: false,
      petDeposit: '0',
      petRent: '0',
      petRestrictions: '',
      parkingType: 'None',
      parkingSpaces: '0',
      parkingDescription: '',
      parkingIncluded: true,
      parkingFee: '0',
      laundryType: 'None',
      laundryDescription: '',
      laundryIncluded: true,
      garageType: 'None',
      garageSpaces: '0',
    });
    
    setMainImage('');
    setPropertyImages([]);
    setUploadProgress(0);
    setSelectedAgent('');
  };

  return (
    <div style={{ color: '#ffffff' }}>
      <style>{`
        .content-container {
          background-color: transparent !important;
        }
        .alert-success {
          background-color: rgba(4, 120, 87, 0.15) !important;
          color: #4ade80 !important;
          border: 1px solid rgba(4, 120, 87, 0.3) !important;
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 20px;
        }
        .alert-danger {
          background-color: rgba(220, 38, 38, 0.15) !important;
          color: #fca5a5 !important;
          border: 1px solid rgba(220, 38, 38, 0.3) !important;
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 20px;
        }
        .form-section {
          background-color: rgba(26, 26, 26, 0.6) !important;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1) !important;
          border-radius: 12px;
          padding: 24px;
          margin-bottom: 24px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        }
        .form-section-header {
          margin-bottom: 20px;
          padding-bottom: 16px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          background-color: transparent !important;
        }
        .form-section-title {
          color: #ffffff !important;
          font-size: 1.25rem;
          font-weight: 600;
          margin: 0;
          background-color: transparent !important;
          text-align: left;
        }
        .form-section-body {
          padding-top: 4px;
          background-color: transparent !important;
        }
        .form-label {
          color: rgba(255, 255, 255, 0.8) !important;
          font-weight: 500;
          margin-bottom: 8px;
          display: block;
        }
        .form-label-required::after {
          content: " *";
          color: #f87171;
        }
        .form-control {
          background-color: rgba(255, 255, 255, 0.05) !important;
          border: 1px solid rgba(255, 255, 255, 0.2) !important;
          color: #ffffff !important;
          padding: 10px 12px;
          border-radius: 6px;
          transition: all 0.2s;
          width: 100%;
        }
        .form-control:focus {
          border-color: rgba(255, 255, 255, 0.4) !important;
          outline: none;
        }
        .form-control::placeholder {
          color: rgba(255, 255, 255, 0.4);
        }
        .form-row {
          display: grid;
          gap: 20px;
          margin-bottom: 20px;
        }
        .form-row-2 {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
          margin-bottom: 20px;
        }
        .form-row-3 {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
          margin-bottom: 20px;
        }
        .form-row-4 {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          margin-bottom: 20px;
        }
        .form-group {
          display: flex;
          flex-direction: column;
        }
        .form-checkbox {
          width: 18px;
          height: 18px;
          margin-right: 8px;
        }
        textarea.form-control {
          min-height: 100px;
          resize: vertical;
        }
        .image-preview-container {
          margin-top: 24px;
        }
        .image-preview-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
          gap: 16px;
          margin-top: 20px;
        }
        .image-preview-item {
          position: relative;
          border-radius: 8px;
          overflow: hidden;
          aspect-ratio: 1;
          background-color: rgba(255, 255, 255, 0.05);
          cursor: move;
        }
        .image-preview-item.being-dragged {
          opacity: 0.5;
        }
        .image-preview-item img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .main-image-badge {
          position: absolute;
          top: 8px;
          left: 8px;
          background-color: rgba(34, 197, 94, 0.9);
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 600;
        }
        .image-actions {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background: linear-gradient(to top, rgba(0,0,0,0.8), transparent);
          padding: 8px;
          display: flex;
          gap: 8px;
          justify-content: center;
        }
        .image-action-button {
          background-color: rgba(255, 255, 255, 0.9);
          color: #000;
          border: none;
          padding: 6px 12px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }
        .image-action-button:hover {
          background-color: #fff;
        }
        .btn-primary {
          background-color: transparent !important;
          color: #ffffff !important;
          border: 1px solid rgba(255, 255, 255, 0.3) !important;
          padding: 12px 32px;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 1rem;
          box-shadow: 0 0 20px rgba(255, 255, 255, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .btn-primary:hover:not(:disabled) {
          background-color: rgba(255, 255, 255, 0.1) !important;
          border-color: rgba(255, 255, 255, 0.5) !important;
          box-shadow: 0 0 30px rgba(255, 255, 255, 0.2);
        }
        .btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .btn-secondary {
          background-color: transparent !important;
          color: #ffffff !important;
          border: 1px solid rgba(255, 255, 255, 0.2) !important;
          padding: 12px 32px;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: 0 0 15px rgba(255, 255, 255, 0.05);
        }
        .btn-secondary:hover {
          background-color: rgba(255, 255, 255, 0.1) !important;
          border-color: rgba(255, 255, 255, 0.4) !important;
        }
        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          margin-top: 32px;
          padding-top: 24px;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }
        .loading-spinner {
          border: 3px solid rgba(255, 255, 255, 0.1) !important;
          border-top-color: rgba(255, 255, 255, 0.8) !important;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-right: 8px;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .text-xs {
          font-size: 0.75rem;
        }
        .text-gray-500 {
          color: rgba(255, 255, 255, 0.5);
        }
        .mt-1 {
          margin-top: 0.25rem;
        }
        .mt-2 {
          margin-top: 0.5rem;
        }
        .mb-2 {
          margin-bottom: 0.5rem;
        }
        .flex {
          display: flex;
        }
        .items-center {
          align-items: center;
        }
        .ml-2 {
          margin-left: 0.5rem;
        }
        @media (max-width: 768px) {
          .form-row-2,
          .form-row-3,
          .form-row-4 {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
      
      <div className="content-container">
        {successMessage && (
          <div className="alert alert-success mb-4">
            {successMessage}
          </div>
        )}
        
        {errorMessage && (
          <div className="alert alert-danger mb-4">
            {errorMessage}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          {/* CSV Import Section */}
          <div className="form-section">
            <div className="form-section-header">
              <h3 className="form-section-title">Import Property Data</h3>
            </div>
            <div className="form-section-body">
              <label className="form-label">Import from CSV</label>
              <p className="text-xs text-gray-500 mb-2">
                Upload a CSV file with property details to pre-populate the form fields.
              </p>
              <input
                type="file"
                accept=".csv"
                onChange={handleCsvFileChange}
                className="form-control"
                style={{ marginBottom: '0.5rem' }}
              />
              
              {csvPreview && (
                <p style={{color: 'rgba(255, 255, 255, 0.7)', fontSize: '13px', marginTop: '0.5rem'}}>
                  <span style={{fontWeight: 600}}>Selected file:</span> {csvPreview.name} ({csvPreview.size})
                </p>
              )}
              
              <button
                type="button"
                onClick={parseCsvFile}
                disabled={!csvFile || isParsingCsv}
                className="btn-secondary"
                style={{ marginTop: '0.5rem' }}
              >
                {isParsingCsv ? 'Parsing...' : 'Import Data'}
              </button>
              
              {csvParseError && (
                <div className="alert alert-danger" style={{ marginTop: '0.5rem' }}>
                  {csvParseError}
                </div>
              )}
            </div>
          </div>

          {/* Basic Information */}
          <div className="form-section">
            <div className="form-section-header">
              <h3 className="form-section-title">Basic Information</h3>
            </div>
            <div className="form-section-body">
              <div className="form-row-3">
                <div className="form-group">
                  <label className="form-label form-label-required">Address</label>
                  <input 
                    type="text" 
                    name="address"
                    value={propertyData.address}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="123 Main St, City, State ZIP"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label form-label-required">Price ($)</label>
                  <input 
                    type="text" 
                    name="price"
                    value={propertyData.price}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="725,000 or 1,500/mo"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label form-label-required">Listing Type</label>
                  <select 
                    name="listingType"
                    value={propertyData.listingType}
                    onChange={handleInputChange}
                    className="form-control"
                    required
                  >
                    <option value="purchase">Purchase</option>
                    <option value="rent">Rent</option>
                  </select>
                </div>
              </div>
              
              <div className="form-row-4">
                <div className="form-group">
                  <label className="form-label form-label-required">Property Type</label>
                  <select 
                    name="propertyType"
                    value={propertyData.propertyType}
                    onChange={handleInputChange}
                    className="form-control"
                    required
                  >
                    <option value="House">House</option>
                    <option value="Condo">Condo</option>
                    <option value="Apartment">Apartment</option>
                    <option value="Townhouse">Townhouse</option>
                    <option value="Multifamily">Multi-Family</option>
                    <option value="ManufacturedMobile">Manufactured/Mobile</option>
                  </select>
                </div>
                  
                <div className="form-group">
                  <label className="form-label form-label-required">Bedrooms</label>
                  <input 
                    type="number" 
                    name="bedrooms"
                    value={propertyData.bedrooms}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="3"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label form-label-required">Bathrooms</label>
                  <input 
                    type="number" 
                    step="0.5"
                    name="bathrooms"
                    value={propertyData.bathrooms}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="2.5"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label form-label-required">Square Feet</label>
                  <input 
                    type="number" 
                    name="sqft"
                    value={propertyData.sqft}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="2,000"
                    required
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label form-label-required">Description</label>
                  <textarea 
                    rows={4}
                    name="description"
                    value={propertyData.description}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Describe the property features, location, and unique selling points..."
                    required
                  ></textarea>
                </div>
              </div>
            </div>
          </div>
          
          {/* Additional Details */}
          <div className="form-section">
            <div className="form-section-header">
              <h3 className="form-section-title">Additional Details</h3>
            </div>
            <div className="form-section-body">
              <div className="form-row-3">
                <div className="form-group">
                  <label className="form-label form-label-required">Neighborhood</label>
                  <select 
                    name="neighborhood"
                    value={propertyData.neighborhood}
                    onChange={handleInputChange}
                    className="form-control"
                    required
                  >
                    <option value="">Select a neighborhood</option>
                    {neighborhoods.map((neighborhood) => (
                      <option 
                        key={neighborhood._id} 
                        value={neighborhood._id}
                      >
                        {neighborhood.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="form-label form-label-required">Year Built</label>
                  <input 
                    type="number" 
                    name="yearBuilt"
                    value={propertyData.yearBuilt}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="2000"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label form-label-required">Lot Size (sq ft)</label>
                  <input 
                    type="number" 
                    name="lotSize"
                    value={propertyData.lotSize}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="5000"
                    required
                  />
                  {propertyData.lotSize && !isNaN(propertyData.lotSize) && (
                    <p className="text-xs text-gray-500 mt-1">
                      ~{(parseFloat(propertyData.lotSize.replace(/[^0-9.]/g, '')) / 43560).toFixed(2)} acres
                    </p>
                  )}
                </div>
              </div>

              {propertyData.listingType === 'purchase' && (
                <div className="form-row-3">
                  <div className="form-group">
                    <label className="form-label form-label-required">HOA Fees ($/mo)</label>
                    <input 
                      type="text" 
                      name="hoaFees"
                      value={propertyData.hoaFees || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="150"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label form-label-required">Property Taxes ($/year)</label>
                    <input 
                      type="text" 
                      name="propertyTax"
                      value={propertyData.propertyTax || ''}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="3200"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Property Status</label>
                    <select 
                      name="propertyStatus"
                      value={propertyData.propertyStatus}
                      onChange={handleInputChange}
                      className="form-control"
                    >
                      <option value="For Sale">For Sale</option>
                      <option value="Pending">Pending</option>
                      <option value="Sold">Sold</option>
                      <option value="Off Market">Off Market</option>
                    </select>
                  </div>
                </div>
              )}
              
              {/* Pet Policy for Rentals */}
              {propertyData.listingType === 'rent' && (
                <>
                  <div className="form-row-3">
                    <div className="form-group">
                      <label className="form-label">Dogs Allowed</label>
                      <div className="flex items-center mt-2">
                        <input
                          type="checkbox"
                          id="allowsDogs"
                          name="allowsDogs"
                          checked={propertyData.allowsDogs}
                          onChange={handleInputChange}
                          className="form-checkbox"
                        />
                        <label htmlFor="allowsDogs" className="ml-2">
                          Property allows dogs
                        </label>
                      </div>
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">Cats Allowed</label>
                      <div className="flex items-center mt-2">
                        <input
                          type="checkbox"
                          id="allowsCats"
                          name="allowsCats"
                          checked={propertyData.allowsCats}
                          onChange={handleInputChange}
                          className="form-checkbox"
                        />
                        <label htmlFor="allowsCats" className="ml-2">
                          Property allows cats
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="form-row-2">
                    <div className="form-group">
                      <label className="form-label">Pet Deposit ($)</label>
                      <input 
                        type="number" 
                        name="petDeposit"
                        value={propertyData.petDeposit}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="0"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">Pet Rent ($/mo)</label>
                      <input 
                        type="number" 
                        name="petRent"
                        value={propertyData.petRent}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </>
              )}
              
              {/* Parking and Laundry */}
              <div className="form-row-3">
                <div className="form-group">
                  <label className="form-label form-label-required">Parking Type</label>
                  <select
                    name="parkingType"
                    value={propertyData.parkingType}
                    onChange={handleInputChange}
                    className="form-control"
                    required
                  >
                    <option value="None">None</option>
                    <option value="Attached">Attached Garage</option>
                    <option value="Detached">Detached Garage</option>
                    <option value="Assigned">Assigned</option>
                    <option value="Carport">Carport</option>
                    <option value="Covered">Covered</option>
                    <option value="Parking Garage">Parking Garage</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Number of Parking Spaces</label>
                  <input
                    type="number"
                    name="parkingSpaces"
                    value={propertyData.parkingSpaces}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="0"
                    min="0"
                  />
                </div>
                
                {propertyData.listingType === 'rent' && (
                  <div className="form-group">
                    <label className="form-label form-label-required">Laundry Type</label>
                    <select
                      name="laundryType"
                      value={propertyData.laundryType}
                      onChange={handleInputChange}
                      className="form-control"
                      required
                    >
                    <option value="None">None</option>
<option value="In Unit">In Unit</option>
<option value="In Building">In Building</option>
<option value="Hookups">Hookups</option>
                    </select>
                  </div>
                )}
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Amenities and Features</label>
                  <input 
                    type="text" 
                    name="features"
                    value={propertyData.features}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Fireplace, Hardwood Floors, Granite Countertops"
                  />
                  <p className="mt-1 text-gray-500 text-xs">
                    Comma-separated list of features
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Agent Information */}
          <div className="form-section">
            <div className="form-section-header">
              <h3 className="form-section-title">Agent Information</h3>
            </div>
            <div className="form-section-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Select Agent</label>
                  <select 
                    name="agent"
                    value={selectedAgent}
                    onChange={handleAgentSelect}
                    className="form-control"
                  >
                    <option value="">Manual entry</option>
                    {agents.map((agent) => (
                      <option 
                        key={agent._id} 
                        value={agent._id}
                      >
                        {agent.name} ({agent.email})
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-gray-500 text-xs">
                    Selecting an agent will auto-fill the fields below
                  </p>
                </div>
              </div>
              
              <div className="form-row-2">
                <div className="form-group">
                  <label className="form-label">Agent Name</label>
                  <input 
                    type="text" 
                    name="agentName"
                    value={propertyData.agentName}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="John Doe"
                    readOnly={!!selectedAgent}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Agency</label>
                  <input 
                    type="text" 
                    name="agency"
                    value={propertyData.agency}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Real Estate Company"
                    readOnly={!!selectedAgent}
                  />
                </div>
              </div>
              
              <div className="form-row-2">
                <div className="form-group">
                  <label className="form-label">Contact Phone</label>
                  <input 
                    type="tel" 
                    name="contactPhone"
                    value={propertyData.contactPhone}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="(555) 123-4567"
                    readOnly={!!selectedAgent}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Contact Email</label>
                  <input 
                    type="email" 
                    name="contactEmail"
                    value={propertyData.contactEmail}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="agent@example.com"
                    readOnly={!!selectedAgent}
                  />
                </div>
              </div>
            </div>
          </div>
          
          {/* Property Images */}
          <div className="form-section">
            <div className="form-section-header">
              <h3 className="form-section-title">Property Images *</h3>
            </div>
            <div className="form-section-body">
              <label className="form-label form-label-required mb-2">
                Upload Images (minimum 5 required)
              </label>
              
              <div 
                className="image-upload-area"
                onMouseEnter={() => setIsUploadAreaHovered(true)}
                onMouseLeave={() => setIsUploadAreaHovered(false)}
                style={{
                  backgroundColor: isUploadAreaHovered ? '#374151' : '#1f2937',
                  border: `2px dashed ${isUploadAreaHovered ? '#6366f1' : '#4b5563'}`,
                  borderRadius: '8px',
                  padding: '2rem',
                  textAlign: 'center',
                  transition: 'all 0.2s'
                }}
              >
                <div className="upload-icon" style={{ color: '#9ca3af', marginBottom: '1rem', display: 'flex', justifyContent: 'center' }}>
                  <svg
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 48 48"
                    aria-hidden="true"
                    width="48"
                    height="48"
                    style={{ color: '#9ca3af', stroke: '#9ca3af' }}
                  >
                    <path
                      d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4h-12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                
                <p className="upload-text" style={{ color: '#d1d5db', fontSize: '0.95rem', marginBottom: '1rem' }}>
                  Drag and drop your images here, or
                </p>
                
                <label 
                  htmlFor="image-upload" 
                  className="upload-button"
                  onMouseEnter={() => setIsButtonHovered(true)}
                  onMouseLeave={() => setIsButtonHovered(false)}
                  style={{
                    display: 'inline-block',
                    padding: '0.5rem 1.5rem',
                    backgroundColor: isButtonHovered ? '#4338ca' : '#4f46e5',
                    color: 'white',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    transition: 'background-color 0.2s'
                  }}
                >
                  Browse Files
                  <input 
                    id="image-upload" 
                    name="image-upload" 
                    type="file" 
                    className="image-upload-input" 
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                  />
                </label>
                
                <p className="upload-hint" style={{ color: '#9ca3af', fontSize: '0.85rem', marginTop: '0.75rem' }}>
                  PNG, JPG, GIF up to 10MB each
                </p>
              </div>
              
              {uploadProgress > 0 && (
                <div className="mt-4">
                  <div style={{ width: '100%', height: '8px', backgroundColor: 'rgba(255, 255, 255, 0.1)', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ width: `${uploadProgress}%`, height: '100%', backgroundColor: '#4f46e5', transition: 'width 0.3s' }}></div>
                  </div>
                  <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', marginTop: '8px', textAlign: 'center' }}>
                    Uploading: {uploadProgress}%
                  </div>
                </div>
              )}
              
              {propertyImages.length > 0 && (
                <div className="image-preview-container">
                  <h4 style={{ fontSize: '1rem', fontWeight: 500, marginBottom: '0.5rem' }}>Uploaded Images</h4>
                  <p className="text-gray-500 text-xs mb-2">
                    Select one image as the main image by clicking "Set as Main". Drag and drop to reorder images.
                  </p>
                  <div className="image-preview-grid">
                    {propertyImages.map((imageUrl, index) => (
                      <div 
                        key={index} 
                        className={`image-preview-item ${draggedImageIndex === index ? 'being-dragged' : ''}`}
                        draggable
                        onDragStart={() => handleDragStart(index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragEnd={handleDragEnd}
                      >
                        {imageUrl === mainImage && (
                          <div className="main-image-badge">Main</div>
                        )}
                        <img
                          src={imageUrl}
                          alt={`Property ${index + 1}`}
                          className="image-preview"
                        />
                        <div className="image-actions">
                          {imageUrl !== mainImage ? (
                            <button
                              type="button"
                              onClick={() => setAsMainImage(imageUrl)}
                              className="image-action-button"
                            >
                              Set as Main
                            </button>
                          ) : (
                            <span className="image-action-button">
                              Main Image
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => removeImage(imageUrl)}
                            className="image-action-button"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="form-actions">
            <button
              type="button"
              onClick={resetForm}
              className="btn-secondary"
            >
              Clear Form
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary"
            >
              {isLoading ? (
                <>
                  <div className="loading-spinner"></div>
                  Saving...
                </>
              ) : 'Save Property'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManualEntryTab;